<template>
  <b-modal
    id="add-new-bank-account"
    ref="add-new-bank-account"
    body-class="p-10"
    centered
    title="Add Bank Account"
    @ok="submitBankAccount"
    @show="setModalData"
    @hidden="resetModal"
  >
    <b-overlay
      :show="isModalLoading"
      variant="white"
      opacity="1"
      rounded="sm"
    >
      <!-- ACH Cardknox Form -->
      <a-c-h-cardknox-template
        v-if="isCardknox"
        ref="cardknoxACHTemplate"
        :i-fields-options="iFieldsOptions"
      />

      <!-- Stripe does not support ACH bank account creation in this application -->
      <div v-if="isStripe" class="text-center py-3">
        <p class="text-muted">
          ACH bank account entry is not available for this payment provider.
          Please contact support for assistance.
        </p>
      </div>

      <!-- Billing Address Form -->
      <billing-address-template
        :editing-mode="false"
        :creating-mode="true"
      />
    </b-overlay>

    <template #modal-footer="{ cancel, ok }">
      <div class="w-100 d-flex justify-content-between">
        <b-button
          class="border border-primary"
          variant="outline-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="isStripe"
          @click="ok()"
        >
          Save
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { getUserData } from '@/auth/utils'
import { mapGetters } from 'vuex'
import store from '@/store'

import { BModal, BButton, BOverlay } from 'bootstrap-vue'
import ACHCardknoxTemplate from '@/components/site/ACHCardknoxTemplate.vue'
import BillingAddressTemplate from '@/components/site/BillingAddressTemplate.vue'

export default {
  name: 'AddNewBankAccount',

  components: {
    BillingAddressTemplate,
    ACHCardknoxTemplate,
    BModal,
    BButton,
    BOverlay,
  },

  data() {
    return {
      userId: getUserData().id,
      cardToken: null,
    }
  },

  computed: {
    ...mapGetters({
      isCardknox: 'paymentSettings/getIsCardknox',
      isStripe: 'paymentSettings/getIsStripe',
      iFieldsOptions: 'paymentSettings/getCardknoxIFieldsOptions',
      billingAddressFormData: 'formValidation/getBillingAddressFormData',
      billingAddressRequiredFields: 'formValidation/getBillingAddressRequiredFields',
    }),

    isModalLoading: {
      get() {
        return store.getters['paymentPortal/getIsModalLoaidng']
      },
      set(val) {
        store.commit('paymentPortal/SET_MODAL_LOADING_STATE', val)
      },
    },

    achName() {
      return this.$store.getters['formValidation/getACHField']('ach_name')
    },

    achRouting() {
      return this.$store.getters['formValidation/getACHField']('ach_routing')
    },
  },

  beforeDestroy() {
    this.clearForms()
  },

  methods: {
    clearForms() {
      this.$store.commit('formValidation/resetBillingAddressForm')
      this.$store.commit('formValidation/resetACHFormData')
    },

    setModalData() {
      // No special initialisation required for ACH
    },

    async validateForm() {
      this.$store.commit('formValidation/resetAllErrors')
      let isValid = true

      const setError = (module, field, value) => {
        this.$store.commit(`formValidation/${module}`, { field, value })
        if (value) isValid = false
      }

      const requireField = (value) => Boolean(value && value.trim())

      // Billing address
      const form = this.billingAddressFormData
      const required = this.billingAddressRequiredFields
      required.forEach(field => {
        setError('setBillingAddressFormError', field, !requireField(form[field]))
      })

      // ACH-specific fields (Cardknox only)
      if (this.isCardknox) {
        setError('setACHFormError', 'ach_name', !requireField(this.achName))
        setError('setACHFormError', 'ach_routing', !requireField(this.achRouting))
      }

      return isValid
    },

    async submitBankAccount(bvModalEvt) {
      bvModalEvt.preventDefault()

      const ok = await this.validateForm()
      if (!ok) return

      if (this.isCardknox) {
        await this.handleCardknoxACHPayment()
      }
    },

    async fetchCardknoxACHToken() {
      return new Promise((resolve) => {
        this.$refs.cardknoxACHTemplate.$refs.ifieldACHRef._onToken = (response) => {
          this.cardToken = response?.data?.xToken || ''
          resolve(this.cardToken)
        }
        this.$refs.cardknoxACHTemplate.$refs.ifieldACHRef.getToken()
      })
    },

    async handleCardknoxACHPayment() {
      if (!this.$refs.cardknoxACHTemplate?.$refs?.ifieldACHRef) return

      await this.fetchCardknoxACHToken()
      if (!this.cardToken) return

      const achNumber = (typeof this.cardToken === 'string' && this.cardToken.includes(';'))
        ? this.cardToken.split(';')[0]
        : ''

      try {
        await this.saveTokenToServer({
          id: this.cardToken,
          type: 'ACH',
          gateway: 'cardknox',
          ach_routing: this.achRouting,
          ach_last4: achNumber.slice(-4),
          ach_name: this.achName,
          billingAddress: this.billingAddressFormData,
        })
      } catch (error) {
        console.error('ACH tokenisation error:', error)
      }
    },

    async saveTokenToServer(token) {
      token.user_id = this.userId

      this.isModalLoading = true
      await store.dispatch('cards/createNewCreditCard', token)
      await store.dispatch('cards/fetchUserCards')
      this.isModalLoading = false

      this.$refs['add-new-bank-account'].hide()
    },

    resetModal() {
      this.cardToken = null
      this.clearForms()
    },
  },
}
</script>

